import { useEffect, useState } from "react";
import api, { API } from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, FileText, Download, Upload } from "lucide-react";

const CATEGORIES = ["legal", "medical", "insurance", "school", "tax", "id", "general"];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("general");
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem("familyos_token");

  const load = async () => {
    const { data } = await api.get("/api/documents");
    setDocs(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !title) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("category", category);
    setUploading(true);
    try {
      await api.post("/api/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded!");
      setFile(null); setTitle(""); setCategory("general"); setOpen(false);
      load();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete document?")) return;
    await api.delete(`/api/documents/${id}`);
    load();
  };

  const colors = { legal: "#E9B5A0", medical: "#B8CFA9", insurance: "#A8BFD1", school: "#E8B96B", tax: "#E9B5A0", id: "#B8CFA9", general: "white" };

  return (
    <div data-testid="documents-page">
      <PageHeader
        title="Document Vault"
        subtitle="Securely store important family documents."
        color="#B8CFA9"
        action={<NBButton testId="upload-doc-btn" color="#B8CFA9" onClick={() => setOpen(true)}><Upload strokeWidth={3} className="w-4 h-4 inline mr-1" />Upload</NBButton>}
      />

      {docs.length === 0 ? (
        <EmptyState title="Vault is empty" subtitle="Upload IDs, insurance, or other important docs." action={<NBButton color="#B8CFA9" onClick={() => setOpen(true)}>Upload</NBButton>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d) => (
            <NBCard key={d.id} color={colors[d.category]} data-testid={`document-${d.id}`}>
              <div className="flex justify-between items-start">
                <FileText strokeWidth={2.5} />
                <button onClick={() => remove(d.id)} className="nb-btn bg-white p-1.5" data-testid={`delete-document-${d.id}`}>
                  <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                </button>
              </div>
              <h4 className="text-lg font-black mt-2 break-words" style={{ fontFamily: "var(--hh-display)" }}>{d.title}</h4>
              <span className="nb-badge bg-white mt-1 inline-block capitalize">{d.category}</span>
              <p className="text-xs mt-2 truncate font-bold">{d.original_filename}</p>
              <p className="text-xs text-neutral-600">{(d.size / 1024).toFixed(1)} KB</p>
              <a
                href={`${API}/documents/${d.id}/download?auth=${token}`}
                target="_blank"
                rel="noreferrer"
                className="nb-btn bg-white mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs"
                data-testid={`download-document-${d.id}`}
              >
                <Download strokeWidth={2.5} className="w-3.5 h-3.5" /> Download
              </a>
            </NBCard>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Upload Document</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="doc-title" required placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
              <NBSelect testId="doc-category" value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </NBSelect>
              <input
                type="file"
                required
                data-testid="doc-file"
                onChange={(e) => setFile(e.target.files?.[0])}
                className="nb-input w-full file:mr-3 file:rounded file:border-2 file:border-black file:bg-[#B8CFA9] file:px-3 file:py-1 file:font-bold"
              />
              <NBButton testId="submit-doc" type="submit" disabled={uploading} color="#B8CFA9" className="w-full">
                {uploading ? "Uploading..." : "Upload"}
              </NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
